import React, { useContext, useState, useEffect } from "react"
import { Accordion, Modal } from "react-bootstrap";
import {
  Button,
  InputLabel,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from "@mui/material";
import { ExpandMore, Close } from "@mui/icons-material";
import AsyncSelect from "react-select/async";
import language from "../../../nls/language.json";
import Context from "../../../context/Context";
import '../Combinations.css';

const MAX_PIECE = 15;

export default function EditCombinationsModal({
  ShowModalEdit,
  setShowModalEdit,
  selectedCombination,
  setSelectedCombination,
  putCombination,
  grupoPecas,
  grupoProdutos,
}) {
  const {
    selectedCountry,
    getFurnace
  } = useContext(Context);

  const [combinacoes, setCombinacoes] = useState([]);

  useEffect(() => {
    if (ShowModalEdit && selectedCombination.isagA_Combinacoes_Pecas) {
      setCombinacoes(
        selectedCombination.isagA_Combinacoes_Pecas.map((item) => ({
          grupo_peca: item.grupo_Peca || "",
          grupo_produto: item.grupo_Produto || "",
        }))
      );
    }
  }, [ShowModalEdit, selectedCombination]);

  const handleFieldChange = (name, value) => {
    setSelectedCombination({
      ...selectedCombination,
      [name]: value
    });
  };

  const handleCombinacaoChange = (index, name, value) => {
    const updated = combinacoes.map((item, i) =>
      i === index ? { ...item, [name]: value } : item
    );
    setCombinacoes(updated);
  };

  const handleAddSlot = () => {
    if (combinacoes.length < MAX_PIECE) {
      setCombinacoes([...combinacoes, { grupo_peca: "", grupo_produto: "" }]);
    }
  };

  const handleRemoveSlot = (index) => {
    if (combinacoes.length > 1) {
      setCombinacoes(combinacoes.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    putCombination({
      ...selectedCombination,
      isagA_Combinacoes_Pecas: combinacoes.map((item) => ({
        grupo_Peca: item.grupo_peca,
        grupo_Produto: item.grupo_produto,
      })),
    });
  };

  const isSaveDisabled =
    !selectedCombination.comb_Nome ||
    !selectedCombination.forno ||
    !combinacoes.length ||
    !combinacoes.every((item) => item.grupo_peca && item.grupo_produto);

  return (
    <Modal show={ShowModalEdit} onHide={() => setShowModalEdit(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {language[selectedCountry.name].Cadastro.combinacoes.modal.editar.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div>
          <div className="d-flex flex-nowrap gap-2">
            <InputLabel className="d-flex flex-column w-50 form-label">
              Combinação
              <TextField
                className="mb-3"
                size="small"
                value={selectedCombination.comb_Nome || ""}
                required
                fullWidth
                placeholder="Nome da combinação"
                onChange={({ target }) => handleFieldChange("comb_Nome", target.value)}
              />
            </InputLabel>

            <InputLabel className="d-flex flex-column w-25 form-label">
              Aproveitamento
              <Select
                size="small"
                value={selectedCombination.aproveitamento ?? 1}
                onChange={({ target }) => handleFieldChange("aproveitamento", target.value)}
              >
                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={0.5}>0.5</MenuItem>
                <MenuItem value={1}>1</MenuItem>
              </Select>
            </InputLabel>

            <InputLabel className="d-flex flex-column w-25 form-label">
              Bomba
              <TextField
                className="mb-3"
                size="small"
                type="number"
                value={selectedCombination.difBomba ?? 0}
                onChange={({ target }) => handleFieldChange("difBomba", Number(target.value))}
                inputProps={{ min: 0 }}
              />
            </InputLabel>
          </div>

          <InputLabel className="form-label">Forno</InputLabel>
          <AsyncSelect
            className="mb-3"
            cacheOptions
            defaultOptions
            placeholder="Forno"
            loadOptions={getFurnace}
            getOptionLabel={(e) => e.forno_Name}
            getOptionValue={(e) => e.forno_Name}
            value={selectedCombination.forno ? { forno_Name: selectedCombination.forno } : null}
            onChange={(e) => handleFieldChange("forno", e.forno_Name)}
          />

          <div className="d-flex align-items-center mb-3">
            <Checkbox
              checked={selectedCombination.ativo ?? true}
              onChange={() => handleFieldChange("ativo", !selectedCombination.ativo)}
            />
            <span className="fw-bold">
              {selectedCombination.ativo ? "ATIVO" : "INATIVO"}
            </span>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <InputLabel className="form-label mb-0">
              Peças da Combinação ({combinacoes.length})
            </InputLabel>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddSlot}
              disabled={combinacoes.length >= MAX_PIECE}
            >
              + Adicionar Peça
            </Button>
          </div>

          <Accordion expanded>
            <AccordionSummary className="header-flex bg-light">
              <p className="mb-0 fw-bold w-50">Grupo Peça</p>
              <p className="mb-0 fw-bold w-50">Grupo Produto</p>
            </AccordionSummary>

            <AccordionDetails className="p-2">
              {combinacoes.map((item, i) => (
                <div className="d-flex align-items-center gap-1 mb-1" key={`edit-comb-${i}`}>
                  <Select
                    size="small"
                    className="w-100"
                    value={item.grupo_peca || ""}
                    displayEmpty
                    onChange={({ target }) => handleCombinacaoChange(i, "grupo_peca", target.value)}
                  >
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    {grupoPecas.map((gp, idx) => (
                      <MenuItem key={`edit-gp-${idx}`} value={gp.prodGrupo_GrupoNome}>
                        {gp.prodGrupo_GrupoNome}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    size="small"
                    className="w-100"
                    value={item.grupo_produto || ""}
                    displayEmpty
                    onChange={({ target }) => handleCombinacaoChange(i, "grupo_produto", target.value)}
                  >
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    {grupoProdutos.map((gpr, idx) => (
                      <MenuItem key={`edit-gpr-${idx}`} value={gpr.prodGrupo_GrupoNome}>
                        {gpr.prodGrupo_GrupoNome}
                      </MenuItem>
                    ))}
                  </Select>

                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveSlot(i)}
                    disabled={combinacoes.length <= 1}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="text"
          sx={{ marginRight: 2 }}
          onClick={() => setShowModalEdit(false)}
        >
          {language[selectedCountry.name].Cadastro.combinacoes.modal.editar.btnCancelar}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {language[selectedCountry.name].Cadastro.combinacoes.modal.editar.btnSalvar}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
